import { useEffect, useState } from "react"
import Icon from "../../../Icon"
import { formatKsh } from "./utilityAdminDisplay"

const PAGE_SIZE = 20

function useShowMore(resetKey) {
  const [showCount, setShowCount] = useState(PAGE_SIZE)
  useEffect(() => setShowCount(PAGE_SIZE), [resetKey])
  return [showCount, () => setShowCount((count) => count + PAGE_SIZE)]
}

function ListIntro({ children }) {
  return <p className="px-3 pb-1 pt-3 text-[11px] leading-snug text-gray-500">{children}</p>
}

function EmptyRow({ children }) {
  return <p className="p-4 text-xs text-gray-400">{children}</p>
}

function ShowMoreButton({ remaining, onClick }) {
  if (remaining <= 0) return null
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full border-t border-gray-100 px-3 py-2.5 text-left text-xs font-medium text-emerald-700 hover:bg-emerald-50/40"
    >
      Show more ({remaining})
    </button>
  )
}

function RoleTag({ className, children }) {
  return (
    <span className={`inline-flex flex-shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${className}`}>
      {children}
    </span>
  )
}

export function UtWalletList({ wallets = [], searchActive = false, onLogin }) {
  const [showCount, showMore] = useShowMore(wallets)
  const visible = wallets.slice(0, showCount)

  return (
    <div>
      <ListIntro>
        Money each person holds in their own Bills &amp; Invoices wallet. A company owner's customer payments land here, so this is where
        an owner's balance shows.
      </ListIntro>
      {wallets.length === 0 ? (
        <EmptyRow>{searchActive ? "No matches." : "No wallet has money yet."}</EmptyRow>
      ) : (
        <div className="divide-y divide-gray-100">
          {visible.map((wallet) => (
            <div key={wallet.userId} className="flex items-center gap-2.5 px-3 py-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-50 text-xs font-bold uppercase text-teal-700">
                {String(wallet.name || "?").trim().charAt(0) || "?"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-gray-900">{wallet.name}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                  {wallet.isCompanyOwner && (
                    <RoleTag className="border-emerald-100 bg-emerald-50 text-emerald-700">Company owner</RoleTag>
                  )}
                  {wallet.isCustomer && <RoleTag className="border-sky-100 bg-sky-50 text-sky-700">Customer</RoleTag>}
                  {wallet.phone && <span className="text-[10px] text-gray-400">{wallet.phone}</span>}
                </div>
                {wallet.companies?.length > 0 && (
                  <p className="mt-0.5 truncate text-[10px] text-gray-500">Owns: {wallet.companies.join(", ")}</p>
                )}
              </div>
              <p className={`flex-shrink-0 text-sm font-bold ${wallet.balance < 0 ? "text-rose-600" : "text-gray-900"}`}>
                {formatKsh(wallet.balance)}
              </p>
              {onLogin && (
                <button
                  type="button"
                  onClick={() => onLogin(wallet.userId)}
                  className="flex-shrink-0 rounded-md border border-emerald-100 px-2 py-1.5 text-[10px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
                  title={`Log in as ${wallet.name}`}
                >
                  Log in
                </button>
              )}
            </div>
          ))}
          <ShowMoreButton remaining={wallets.length - showCount} onClick={showMore} />
        </div>
      )}
    </div>
  )
}

export function PropertyWalletList({ wallets = [], searchActive = false }) {
  const [showCount, showMore] = useShowMore(wallets)
  const visible = wallets.slice(0, showCount)

  return (
    <div>
      <ListIntro>
        Older Rentals utility income kept for each property's landlord. This money is not in anyone's Bills &amp; Invoices wallet.
      </ListIntro>
      {wallets.length === 0 ? (
        <EmptyRow>{searchActive ? "No matches." : "No landlord property wallets yet."}</EmptyRow>
      ) : (
        <div className="divide-y divide-gray-100">
          {visible.map((wallet) => (
            <div key={wallet.id} className="flex items-center gap-2.5 px-3 py-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <Icon name="house" size="0.9rem" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-gray-900">{wallet.propertyName}</p>
                <p className="truncate text-[10px] text-gray-500">Landlord: {wallet.landlordName}</p>
              </div>
              <p className={`flex-shrink-0 text-sm font-bold ${wallet.balance < 0 ? "text-rose-600" : "text-gray-900"}`}>
                {formatKsh(wallet.balance)}
              </p>
            </div>
          ))}
          <ShowMoreButton remaining={wallets.length - showCount} onClick={showMore} />
        </div>
      )}
    </div>
  )
}
